import React, { useState } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MapPin, Phone, Calendar, Mail, ArrowRight } from "lucide-react";
import { createContactMessage } from "@/lib/api";
import { Fleuron, OrnamentDivider } from "@/components/Ornament";
import { PageHeaderBanner } from "@/components/PageHeaderBanner";
import { ScrollReveal } from "@/components/ScrollReveal";

const fieldClass =
  "w-full bg-transparent border-0 border-b border-border rounded-none px-0 py-3 focus-visible:ring-0 focus-visible:border-primary transition-colors text-base placeholder:text-foreground/40";

const Contact = () => {
  const { t } = useLanguage();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const handleChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error(t("contact.form.error"));
      return;
    }
    try {
      setSubmitting(true);
      await createContactMessage({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        subject: form.subject.trim() || undefined,
        message: form.message.trim(),
      });
      toast.success(t("contact.form.success"));
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(t("contact.form.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pb-24 bg-sky-roze paper-grain relative overflow-hidden">
      <PageHeaderBanner
        testid="contact-header-banner"
        imgSrc="/illustrations/contact_facade.png"
        eyebrow={t("contact.eyebrow")}
        title={t("contact.title")}
      />

      <section className="container-roze relative pt-16 md:pt-24">

        <OrnamentDivider className="mb-16" />

        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          {/* Info column */}
          <ScrollReveal as="aside" className="md:col-span-5 space-y-10">
            <div>
              <div className="overline mb-3">{t("contact.address_label")}</div>
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 mt-1 text-primary" strokeWidth={1.4} />
                <p className="font-serif text-2xl md:text-3xl font-light leading-snug">
                  {t("contact.address_value")}
                </p>
              </div>
            </div>

            <div>
              <div className="overline mb-3">{t("contact.phone_label")}</div>
              <a
                href="tel:+37126111722"
                data-testid="contact-phone-link"
                className="flex items-center gap-3 group"
              >
                <Phone className="h-5 w-5 text-primary" strokeWidth={1.4} />
                <span className="font-serif text-2xl md:text-3xl font-light group-hover:text-primary transition-colors">
                  +371 26 111 722
                </span>
              </a>
            </div>

            <div>
              <div className="overline mb-3">{t("contact.hours_label")}</div>
              <div className="flex items-start gap-3">
                <Calendar className="h-5 w-5 mt-1 text-primary" strokeWidth={1.4} />
                <p className="text-base md:text-lg text-foreground/80 leading-relaxed">
                  {t("contact.hours_value")}
                </p>
              </div>
            </div>
          </ScrollReveal>

          {/* Form column */}
          <ScrollReveal delay={1} className="md:col-span-7">
            <div className="bg-white/70 border border-gold/40 p-8 md:p-10 paper-grain">
              <h2 className="font-serif text-3xl md:text-4xl font-light mb-8 italic">
                {t("contact.form_title")}
              </h2>

              <form
                data-testid="contact-form"
                onSubmit={handleSubmit}
                className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6"
              >
              <div className="md:col-span-1">
                <label className="overline block mb-2">{t("contact.form.name")}</label>
                <Input
                  data-testid="contact-input-name"
                  className={fieldClass}
                  value={form.name}
                  onChange={handleChange("name")}
                  required
                />
              </div>
              <div className="md:col-span-1">
                <label className="overline block mb-2">{t("contact.form.email")}</label>
                <Input
                  type="email"
                  data-testid="contact-input-email"
                  className={fieldClass}
                  value={form.email}
                  onChange={handleChange("email")}
                  required
                />
              </div>
              <div className="md:col-span-1">
                <label className="overline block mb-2">{t("contact.form.phone")}</label>
                <Input
                  data-testid="contact-input-phone"
                  className={fieldClass}
                  value={form.phone}
                  onChange={handleChange("phone")}
                />
              </div>
              <div className="md:col-span-1">
                <label className="overline block mb-2">{t("contact.form.subject")}</label>
                <Input
                  data-testid="contact-input-subject"
                  className={fieldClass}
                  value={form.subject}
                  onChange={handleChange("subject")}
                />
              </div>
              <div className="md:col-span-2">
                <label className="overline block mb-2">{t("contact.form.message")}</label>
                <Textarea
                  data-testid="contact-input-message"
                  className={`${fieldClass} min-h-[140px] resize-none`}
                  rows={5}
                  value={form.message}
                  onChange={handleChange("message")}
                  required
                />
              </div>
              <div className="md:col-span-2 flex items-center justify-between gap-4 mt-2">
                <a
                  href="mailto:rozerestcatering@gmail.com"
                  data-testid="contact-email-link"
                  className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-foreground/60 hover:text-foreground"
                >
                  <Mail className="h-3.5 w-3.5" />
                  rozerestcatering@gmail.com
                </a>
                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={submitting}
                  className="group inline-flex items-center gap-3 px-8 py-4 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-xs uppercase tracking-[0.22em] disabled:opacity-50"
                >
                  {submitting ? "..." : t("contact.form.submit")}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
                </button>
              </div>
              </form>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
};

export default Contact;
